import { captureCustomizerMessage } from "./sentry";

// Every backend call of the customizer reports its failure here, once. Callers name the
// feature (`customizerFeature` in the axios config, `feature` for reportingFetch) instead
// of reporting the same failure again. Calls whose failure is already reported with more
// context elsewhere (checkout, draft loading, preview outcome, retried polling) opt out with
// `sentryReport: false` and a `sentry-ignore:` comment saying who reports it.

const describeUrl = (url) => {
    try {
        const parsed = new URL(String(url), window.location.origin);
        // Query and hash can carry customer data; the path identifies the webhook.
        return { host: parsed.host || "unknown", path: parsed.pathname || "/" };
    } catch (_error) {
        // sentry-ignore: unparsable URL only affects diagnostic tags
        return { host: "invalid", path: "invalid" };
    }
};

// Short, non-personal reason codes some webhooks return (e.g. the upload workflow).
const getBackendErrorCode = (data) => {
    const code = data?.uploadValidationError || data?.errorCode;
    return typeof code === "string" && /^[a-z0-9_]{1,60}$/i.test(code) ? code : "none";
};

export const reportBackendRequestFailure = ({
    method,
    url,
    status,
    code,
    durationMs,
    client,
    feature,
    backendErrorCode = "none",
    diagnostics = {},
}) => {
    const { host, path } = describeUrl(url);
    const featureName = feature || "unspecified";

    captureCustomizerMessage("customizer_backend_request_failed", {
        level: "error",
        // One issue per feature and endpoint, so a newly failing feature raises a new alert.
        fingerprint: ["customizer_backend_request_failed", featureName, host, path],
        tags: {
            feature: featureName,
            client,
            http_method: String(method || "get").toUpperCase(),
            request_host: host,
            request_path: path.slice(0, 120),
            http_status: String(status || "none"),
            error_code: code || "unknown",
            backend_error_code: backendErrorCode,
        },
        extra: { duration_ms: durationMs ?? null, ...diagnostics },
    });
};

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

export const installAxiosReporting = (axiosInstance) => {
    axiosInstance.interceptors.request.use((config) => {
        config.customizerRequestStartedAt = now();
        return config;
    });

    axiosInstance.interceptors.response.use(
        (response) => response,
        (error) => {
            const config = error?.config || {};
            const cancelled = axiosInstance.isCancel?.(error) || error?.code === "ERR_CANCELED";

            if (!cancelled && config.sentryReport !== false) {
                reportBackendRequestFailure({
                    client: "axios",
                    feature: config.customizerFeature,
                    method: config.method,
                    url: config.url,
                    status: error?.response?.status,
                    code: error?.code,
                    durationMs: config.customizerRequestStartedAt
                        ? Math.round(now() - config.customizerRequestStartedAt)
                        : null,
                    backendErrorCode: getBackendErrorCode(error?.response?.data),
                    diagnostics: config.customizerDiagnostics,
                });
            }

            return Promise.reject(error);
        }
    );
};

// Use instead of window.fetch in the customizer: reports network errors and non-2xx
// answers, then behaves exactly like fetch (resolves with the response or rethrows).
export const reportingFetch = async (url, init = {}, { sentryReport = true, feature } = {}) => {
    const startedAt = now();

    try {
        const response = await window.fetch(url, init);
        if (!response.ok && sentryReport) {
            reportBackendRequestFailure({
                client: "fetch",
                feature,
                method: init.method,
                url,
                status: response.status,
                code: "HTTP_ERROR",
                durationMs: Math.round(now() - startedAt),
            });
        }
        return response;
    } catch (error) {
        if (sentryReport && error?.name !== "AbortError") {
            reportBackendRequestFailure({
                client: "fetch",
                feature,
                method: init.method,
                url,
                status: null,
                code: error?.name || "NETWORK_ERROR",
                durationMs: Math.round(now() - startedAt),
            });
        }
        throw error;
    }
};
